import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useField, useFormikContext } from "formik";
import axios from "axios";
import {
  indexTextItemForSearch,
  matchTextItemWithQuery,
  getCachedTransliteration,
  fetchTransliteration,
} from "../helpers/tamilPhonetics";

const AREA_CACHE_PREFIX = "dwt_village_areas_";

/**
 * VillageAreaInput
 * Autocomplete combobox showing areas related to the selected village from the DB.
 * Live filtering supporting phonetic Tanglish & Tamil search.
 * Keyboard accessible (↑↓ navigate, ↵ select, esc close).
 * Auto-advances focus to nextRef (City input) upon selection.
 */
const VillageAreaInput = ({
  id = "area",
  name = "area",
  placeholder = "Area / Locality",
  className = "",
  innerRef,
  nextRef,
  moiList = [],
  apiGet = null,
  onSelect = null,
  ...props
}) => {
  const [field, , helpers] = useField(name);
  const { values } = useFormikContext();
  const currentVillage = (values?.village || "").trim();

  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [apiSuggestions, setApiSuggestions] = useState([]);

  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);

  // Helper to get auth token
  const getAuthToken = useCallback(() => {
    return (
      localStorage.getItem("dwt-entry-tok") ||
      localStorage.getItem("dwt-tok") ||
      localStorage.getItem("dwt-cust-tok") ||
      localStorage.getItem("access_token") ||
      localStorage.getItem("org_access_token") ||
      localStorage.getItem("token") ||
      ""
    );
  }, []);

  // Fetch areas related to current village (empty if no village selected or new unlisted village)
  useEffect(() => {
    let isMounted = true;

    if (!currentVillage) {
      setAreas([]);
      setShowDropdown(false);
      setHighlightedIndex(-1);
      return;
    }

    const normVillage = currentVillage.toLowerCase();
    const cacheKey = `${AREA_CACHE_PREFIX}${normVillage}`;

    // 1. Initial hydrate from local cache
    try {
      const cached = JSON.parse(localStorage.getItem(cacheKey) || "[]");
      if (Array.isArray(cached) && cached.length > 0 && isMounted) {
        setAreas(cached);
      }
    } catch (e) {}

    const loadAreas = async () => {
      setLoading(true);
      try {
        let remoteList = [];
        const endpoint = `entry/areas-by-village?village=${encodeURIComponent(currentVillage)}`;

        if (typeof apiGet === "function") {
          const res = await apiGet(endpoint);
          if (res?.data?.data && Array.isArray(res.data.data)) {
            remoteList = res.data.data;
          }
        } else {
          const token = getAuthToken();
          const rawBaseUrl =
            process.env.REACT_APP_API_URL ||
            process.env.REACT_APP_BACKEND_URL ||
            "http://localhost:8000";
          const baseUrl = rawBaseUrl.replace(/\/+$/, "");
          const res = await axios.get(`${baseUrl}/${endpoint.replace(/^\/+/, "")}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          if (res.data?.data && Array.isArray(res.data.data)) {
            remoteList = res.data.data;
          }
        }

        // Also harvest areas from local moiList strictly for this village
        const localAreas = (moiList || [])
          .filter(
            (m) =>
              m.village &&
              m.village.trim().toLowerCase() === normVillage &&
              m.area &&
              m.area.trim() !== ""
          )
          .map((m) => m.area.trim());

        // Merge & deduplicate
        const merged = Array.from(
          new Set(
            [...remoteList, ...localAreas]
              .map((a) => (typeof a === "string" ? a.trim() : ""))
              .filter(Boolean)
          )
        );

        if (isMounted) {
          merged.sort((a, b) => a.localeCompare(b, "ta"));
          setAreas(merged);
          try {
            localStorage.setItem(cacheKey, JSON.stringify(merged));
          } catch (e) {}
        }
      } catch (err) {
        console.warn("Failed to fetch areas for village:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    const timer = setTimeout(loadAreas, 150);

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [currentVillage, apiGet, getAuthToken, moiList]);

  // Pre-index areas for O(1) phonetic Tanglish and Tamil searching
  const indexedAreas = useMemo(() => {
    return (areas || []).map((a) => indexTextItemForSearch(a));
  }, [areas]);

  // Debounced API transliteration for English search queries
  useEffect(() => {
    const raw = (field.value || "").trim();
    if (!raw || !/[a-zA-Z]/.test(raw)) {
      setApiSuggestions([]);
      return;
    }

    const cached = getCachedTransliteration(raw);
    if (cached) {
      setApiSuggestions(cached);
      return;
    }

    const timer = setTimeout(async () => {
      const suggestions = await fetchTransliteration(raw);
      setApiSuggestions(suggestions);
    }, 200);

    return () => clearTimeout(timer);
  }, [field.value]);

  // Filter areas based on current input
  const filteredAreas = useMemo(() => {
    const query = (field.value || "").trim();
    if (!query) {
      return indexedAreas;
    }
    return indexedAreas.filter((a) =>
      matchTextItemWithQuery(a, query, apiSuggestions)
    );
  }, [field.value, indexedAreas, apiSuggestions]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // Suppress transliteration popup when dropdown is visible
  useEffect(() => {
    const inputEl = innerRef?.current || wrapperRef.current?.querySelector("input");
    if (!inputEl) return;

    if (showDropdown && filteredAreas.length > 0) {
      inputEl.dataset.suppressTranslate = "true";
      inputEl.classList.add("suppress-translate");
      document.dispatchEvent(new CustomEvent("dismiss-tamil-suggestions"));
    } else {
      const wasSuppressed = inputEl.dataset.suppressTranslate === "true";
      inputEl.dataset.suppressTranslate = "false";
      inputEl.classList.remove("suppress-translate");
      if (wasSuppressed && /[a-zA-Z]/.test(inputEl.value || "")) {
        inputEl.dispatchEvent(
          new CustomEvent("request-tamil-suggestion", { bubbles: true })
        );
      }
    }
  }, [showDropdown, filteredAreas.length, innerRef]);

  // Auto-scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const highlightedEl = dropdownRef.current.querySelector(
        `[data-area-index="${highlightedIndex}"]`
      );
      if (highlightedEl) {
        highlightedEl.scrollIntoView({ block: "nearest" });
      }
    }
  }, [highlightedIndex]);

  // Handle Area selection
  const handleSelectArea = useCallback(
    (areaItem) => {
      const selectedArea = typeof areaItem === "string" ? areaItem : areaItem.raw || "";
      helpers.setValue(selectedArea);
      setShowDropdown(false);
      setHighlightedIndex(-1);

      document.dispatchEvent(new CustomEvent("dismiss-tamil-suggestions"));
      const inputEl = innerRef?.current || wrapperRef.current?.querySelector("input");
      if (inputEl) {
        inputEl.dataset.suppressTranslate = "false";
        inputEl.classList.remove("suppress-translate");
      }

      if (typeof onSelect === "function") {
        onSelect(selectedArea);
      }

      // Smoothly advance focus to City input
      setTimeout(() => {
        nextRef?.current?.focus();
      }, 50);
    },
    [helpers, innerRef, nextRef, onSelect]
  );

  // Key navigation
  const handleKeyDown = (e) => {
    if (showDropdown && filteredAreas.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredAreas.length - 1 ? prev + 1 : 0
        );
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredAreas.length - 1
        );
        return;
      }
      if (e.key === "Enter") {
        if (highlightedIndex >= 0 && filteredAreas[highlightedIndex]) {
          e.preventDefault();
          e.stopPropagation();
          handleSelectArea(filteredAreas[highlightedIndex]);
          return;
        } else {
          // If no item highlighted, user pressed enter with typed text
          setShowDropdown(false);
          setHighlightedIndex(-1);
          if (nextRef?.current) {
            e.preventDefault();
            e.stopPropagation();
            nextRef.current.focus();
            return;
          }
        }
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setShowDropdown(false);
        setHighlightedIndex(-1);
        return;
      }
    } else {
      if (e.key === "Enter") {
        if (nextRef?.current) {
          e.preventDefault();
          e.stopPropagation();
          nextRef.current.focus();
        }
      }
    }
  };

  const isVisible = showDropdown && filteredAreas.length > 0;

  return (
    <div
      ref={wrapperRef}
      className="relative w-full village-area-combobox"
      onFocusCapture={() => {
        if (currentVillage && areas.length > 0) {
          setShowDropdown(true);
        }
      }}
      onInputCapture={() => {
        if (currentVillage && areas.length > 0) {
          setShowDropdown(true);
        }
      }}
    >
      <input
        {...field}
        {...props}
        ref={innerRef}
        id={id}
        name={name}
        type="text"
        placeholder={placeholder}
        autoComplete="off"
        className={className}
        onKeyDown={handleKeyDown}
        aria-autocomplete="list"
        aria-expanded={isVisible}
        role="combobox"
        aria-controls={isVisible ? "village-areas-listbox" : undefined}
      />

      {isVisible && (
        <div
          ref={dropdownRef}
          role="listbox"
          id="village-areas-listbox"
          className="absolute z-50 left-0 top-full mt-1.5 w-full min-w-[280px] max-h-72 bg-white/95 backdrop-blur-md rounded-2xl border-2 border-indigo-200 shadow-2xl overflow-hidden flex flex-col animation-in fade-in slide-in-from-top-1 duration-150"
        >
          {/* Header Bar */}
          <div className="bg-gradient-to-r from-indigo-50 to-indigo-100/70 px-3 py-2 border-b border-indigo-100 flex items-center justify-between text-xs">
            <div className="flex items-center gap-1.5 text-indigo-900 font-bold truncate">
              <span className="text-sm">📍</span>
              <span className="truncate">{currentVillage || "All Areas"}</span>
              {loading ? (
                <span className="inline-block w-3 h-3 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin shrink-0" />
              ) : (
                <span className="bg-indigo-600 text-white text-[10px] px-1.5 py-0.5 rounded-full font-extrabold shrink-0">
                  {filteredAreas.length}
                </span>
              )}
            </div>
            <span className="text-[10px] text-indigo-500 font-medium hidden sm:inline shrink-0">
              ↑↓ navigate • ↵ select • esc close
            </span>
          </div>

          {/* Area List */}
          <div className="overflow-y-auto max-h-60 divide-y divide-gray-100 py-1">
            {filteredAreas.map((item, idx) => {
              const isHighlighted = idx === highlightedIndex;
              const areaName = typeof item === "string" ? item : item.raw || "";

              return (
                <div
                  key={`${areaName}-${idx}`}
                  data-area-index={idx}
                  role="option"
                  aria-selected={isHighlighted}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelectArea(item);
                  }}
                  className={`px-3 py-2 cursor-pointer transition-all flex items-center justify-between gap-2 select-none ${
                    isHighlighted
                      ? "bg-indigo-50 border-l-4 border-indigo-600 pl-2.5 shadow-sm"
                      : "hover:bg-gray-50/80"
                  }`}
                >
                  <span className="text-sm font-bold text-gray-900 truncate">
                    {areaName}
                  </span>
                  <span className="text-[10px] font-semibold text-indigo-600 bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded shrink-0">
                    பகுதி
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default VillageAreaInput;
